import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ChevronLeft, ChevronRight, Pause, Play, ShieldCheck, Lock, Fingerprint, Check, Map, MapPin,
  Users, Calendar, Megaphone, QrCode, Activity, FileText, Scale, ExternalLink,
} from "lucide-react";

const ICONS = {
  shield: ShieldCheck, lock: Lock, fingerprint: Fingerprint, check: Check, map: Map,
  "map-pin": MapPin, users: Users, calendar: Calendar, megaphone: Megaphone,
  "qr-code": QrCode, activity: Activity, "file-text": FileText, scale: Scale,
};

const OVERLAYS = {
  left: {
    light: "bg-gradient-to-r from-slate-950/65 via-slate-950/35 to-slate-950/10",
    medium: "bg-gradient-to-r from-slate-950/80 via-slate-950/50 to-slate-950/15",
    strong: "bg-gradient-to-r from-slate-950/90 via-slate-950/65 to-slate-950/25",
  },
  right: {
    light: "bg-gradient-to-l from-slate-950/65 via-slate-950/35 to-slate-950/10",
    medium: "bg-gradient-to-l from-slate-950/80 via-slate-950/50 to-slate-950/15",
    strong: "bg-gradient-to-l from-slate-950/90 via-slate-950/65 to-slate-950/25",
  },
  center: {
    light: "bg-slate-950/50",
    medium: "bg-slate-950/65",
    strong: "bg-slate-950/75",
  },
};

const FALLBACK = [{
  id: "fallback",
  label: "ELECTION TRANSPARENCY",
  headline: "Witness Your Vote.",
  subheadline: "Create an Independent Record of Your Vote.",
  description: "Create a secure, independent declaration of how you say you voted through the official election process, receive a verifiable receipt, and help create another layer of public election transparency. Witness Your Vote does not replace or cast your official ballot.",
  image_desktop: null, image_mobile: null,
  image_alt: "Witness Your Vote Ontario",
  ctas: [
    { text: "Witness My Vote", url: "/declare", enabled: true, external: false, new_tab: false },
    { text: "How It Works", url: "/how-it-works", enabled: true, external: false, new_tab: false },
    { text: "Verify My Receipt", url: "/verify", enabled: true, external: false, new_tab: false },
  ],
  feature_points: [
    { icon: "shield", text: "Independent" }, { icon: "lock", text: "Private" },
    { icon: "fingerprint", text: "Verifiable" }, { icon: "check", text: "Integrity Protected" },
  ],
  text_align: "left", overlay_strength: "strong",
}];

const CtaButton = ({ cta, variant, testId }) => {
  const base =
    "inline-flex items-center justify-center gap-1.5 rounded-lg font-semibold text-sm h-11 px-5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white transition-colors duration-150";
  const styles = {
    primary: "bg-white text-slate-900 hover:bg-slate-100",
    secondary: "border-2 border-white/80 text-white hover:bg-white/10",
    tertiary: "text-white underline underline-offset-4 decoration-white/50 hover:decoration-white px-3",
  };
  const cls = `${base} ${styles[variant]}`;
  if (cta.external) {
    return (
      <a href={cta.url} className={cls} data-testid={testId}
         target={cta.new_tab ? "_blank" : undefined}
         rel={cta.new_tab ? "noopener noreferrer" : undefined}>
        {cta.text} {cta.external && <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />}
      </a>
    );
  }
  return (
    <Link to={cta.url} className={cls} data-testid={testId}>
      {cta.text}
    </Link>
  );
};

export default function HeroCarousel() {
  const [heroes, setHeroes] = useState(null);
  const [config, setConfig] = useState({ auto_rotate: true, duration_seconds: 8 });
  const [index, setIndex] = useState(0);
  const [hoverPaused, setHoverPaused] = useState(false);
  const [manualPaused, setManualPaused] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [loaded, setLoaded] = useState(new Set([0]));
  const touchX = useRef(null);
  const regionRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const onChange = (e) => setReducedMotion(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    api.get("/public/heroes")
      .then((r) => {
        setHeroes(r.data.heroes?.length ? r.data.heroes : FALLBACK);
        if (r.data.config) setConfig(r.data.config);
      })
      .catch(() => setHeroes(FALLBACK));
  }, []);

  const count = heroes?.length || 0;

  const go = useCallback((next) => {
    if (!count) return;
    const target = ((next % count) + count) % count;
    setIndex(target);
    setLoaded((prev) => {
      const s = new Set(prev);
      s.add(target);
      s.add((target + 1) % count);
      return s;
    });
  }, [count]);

  // preload the next slide shortly after mount
  useEffect(() => {
    if (!count) return;
    const t = setTimeout(() => setLoaded((p) => new Set(p).add(1 % count)), 2500);
    return () => clearTimeout(t);
  }, [count]);

  // auto-rotation (pauses on interaction / reduced motion)
  useEffect(() => {
    if (!count || count < 2) return;
    if (!config.auto_rotate || hoverPaused || manualPaused || reducedMotion) return;
    const ms = Math.max(4, Math.min(30, config.duration_seconds || 8)) * 1000;
    const t = setInterval(() => go(index + 1), ms);
    return () => clearInterval(t);
  }, [count, config, hoverPaused, manualPaused, reducedMotion, index, go]);

  if (!heroes) {
    return <div className="w-full h-[560px] sm:h-[600px] lg:h-[660px] bg-slate-900" aria-hidden="true" />;
  }

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") { e.preventDefault(); go(index - 1); }
    if (e.key === "ArrowRight") { e.preventDefault(); go(index + 1); }
  };

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; setHoverPaused(true); };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(delta) > 50) go(delta < 0 ? index + 1 : index - 1);
    touchX.current = null;
    setTimeout(() => setHoverPaused(false), 3000);
  };

  return (
    <section
      ref={regionRef}
      role="region"
      aria-roledescription="carousel"
      aria-label="Witness Your Vote highlights"
      tabIndex={0}
      onKeyDown={onKeyDown}
      onMouseEnter={() => setHoverPaused(true)}
      onMouseLeave={() => setHoverPaused(false)}
      onFocus={() => setHoverPaused(true)}
      onBlur={() => setHoverPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      className="relative w-full h-[560px] sm:h-[600px] lg:h-[660px] bg-slate-900 overflow-hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent"
      data-testid="hero-carousel"
    >
      {heroes.map((hero, i) => {
        const active = i === index;
        const align = hero.text_align || "left";
        const overlay = OVERLAYS[align]?.[hero.overlay_strength || "medium"] || OVERLAYS.left.medium;
        const showImg = loaded.has(i) && (hero.image_desktop || hero.image_mobile);
        const enabledCtas = (hero.ctas || []).filter((c) => c.enabled && c.text && c.url);
        const alignCls =
          align === "center" ? "items-center text-center mx-auto" :
          align === "right" ? "items-end text-right ml-auto" : "items-start text-left";
        return (
          <div
            key={hero.id || i}
            role="group"
            aria-roledescription="slide"
            aria-label={`Slide ${i + 1} of ${count}: ${hero.headline}`}
            aria-hidden={!active}
            inert={!active ? "" : undefined}
            className={`absolute inset-0 ${reducedMotion ? "" : "transition-opacity duration-700 ease-out"} ${
              active ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"
            }`}
            data-testid={`hero-slide-${i}`}
          >
            {showImg && (
              <picture>
                {hero.image_mobile && <source media="(max-width: 640px)" srcSet={hero.image_mobile} />}
                <img
                  src={hero.image_desktop || hero.image_mobile}
                  alt={hero.image_alt || hero.headline}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading={i === 0 ? "eager" : "lazy"}
                  fetchPriority={i === 0 ? "high" : "low"}
                  decoding="async"
                />
              </picture>
            )}
            <div className={`absolute inset-0 ${overlay}`} aria-hidden="true" />

            <div className="relative h-full wyv-container flex items-center">
              <div className={`flex flex-col max-w-2xl ${alignCls} pb-16 sm:pb-10`}>
                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 px-3.5 py-1.5 text-[11px] font-bold tracking-[0.2em] text-white uppercase mb-5">
                  {hero.label}
                </span>
                <h1 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl leading-[1.06] text-white">
                  {hero.headline}
                </h1>
                {hero.subheadline && (
                  <p className="font-heading font-semibold text-lg sm:text-2xl text-emerald-300 mt-3">
                    {hero.subheadline}
                  </p>
                )}
                {hero.description && (
                  <p className="text-sm sm:text-base text-slate-200 leading-relaxed mt-4 max-w-xl">
                    {hero.description}
                  </p>
                )}
                {enabledCtas.length > 0 && (
                  <div className="flex flex-wrap gap-3 mt-7">
                    {enabledCtas.map((cta, ci) => (
                      <CtaButton
                        key={ci}
                        cta={cta}
                        variant={ci === 0 ? "primary" : ci === 1 ? "secondary" : "tertiary"}
                        testId={`hero-${i}-cta-${ci + 1}`}
                      />
                    ))}
                  </div>
                )}
                {(hero.feature_points || []).length > 0 && (
                  <ul className="flex flex-wrap gap-x-5 gap-y-2 mt-7" aria-label="Highlights">
                    {hero.feature_points.map((fp, fi) => {
                      const Icon = ICONS[fp.icon] || Check;
                      return (
                        <li key={fi} className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/90">
                          <Icon className="h-3.5 w-3.5 text-emerald-300" aria-hidden="true" /> {fp.text}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {count > 1 && (
        <>
          <button
            type="button"
            onClick={() => go(index - 1)}
            aria-label="Previous slide"
            data-testid="hero-prev-button"
            className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full bg-white/10 hover:bg-white/25 backdrop-blur-sm border border-white/25 flex items-center justify-center text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => go(index + 1)}
            aria-label="Next slide"
            data-testid="hero-next-button"
            className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 z-20 h-11 w-11 rounded-full bg-white/10 hover:bg-white/25 backdrop-blur-sm border border-white/25 flex items-center justify-center text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          <div className="absolute bottom-5 left-0 right-0 z-20 flex items-center justify-center gap-2" data-testid="hero-dots">
            {heroes.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => go(i)}
                aria-label={`Go to slide ${i + 1}`}
                aria-current={i === index ? "true" : undefined}
                data-testid={`hero-dot-${i}`}
                className={`h-2.5 rounded-full transition-all duration-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white ${
                  i === index ? "w-7 bg-white" : "w-2.5 bg-white/40 hover:bg-white/70"
                }`}
              />
            ))}
            <button
              type="button"
              onClick={() => setManualPaused((p) => !p)}
              aria-label={manualPaused ? "Resume automatic rotation" : "Pause automatic rotation"}
              data-testid="hero-pause-button"
              className="ml-3 h-8 w-8 rounded-full bg-white/10 hover:bg-white/25 border border-white/25 flex items-center justify-center text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
            >
              {manualPaused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
